import { useEffect } from 'react';
import { MapContainer, TileLayer, Polyline, CircleMarker, Marker, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import MapFullscreen from './MapFullscreen.jsx';

// A map of one or more routes: road lines, numbered stops and the vans'
// homes. Used by Plan the day (office) and Today's schedule (app).
//
// Coordinates arrive as [lon, lat] (what the API and router speak) for
// points, and [lat, lon] for lines (what the API decodes them to).
//
//   routes: [{ key, colour, line: [[lat, lon]...], dashed, casing }]
//           casing: an outline colour drawn under the line (for light colours)
//   stops:  [{ key, coord: [lon, lat], label, colour, title, onClick }]
//   homes:  [{ key, coord: [lon, lat], title, colour }]
//   simple: the app's map: no zoom buttons (pinch still works)

const numberIcon = (label, colour) => L.divIcon({
  className: 'map-stop',
  html: `<span style="background:${colour}">${label}</span>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

function Fit({ points }) {
  const map = useMap();
  const key = points.map((p) => p.join(',')).join(';');
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) map.setView(points[0], 13);
    else map.fitBounds(points, { padding: [28, 28], maxZoom: 14 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, map]);
  return null;
}

// fullscreen: a button in the corner to make the map fill the screen.
export default function RouteMap({ routes = [], stops = [], homes = [], height = 360, interactive = true, simple = false, className = '', fullscreen = false }) {
  const toLatLon = ([lon, lat]) => [lat, lon];
  const bounds = [
    ...stops.filter((s) => s.coord).map((s) => toLatLon(s.coord)),
    ...homes.filter((h) => h.coord).map((h) => toLatLon(h.coord)),
  ];
  // England, until there is something to show.
  const centre = bounds[0] || [52.9, -1.5];

  return (
    <div className={`route-map${simple ? ' route-map-simple' : ''} ${className}`} style={{ height }}>
      <MapContainer
        center={centre}
        zoom={bounds.length ? 12 : 6}
        scrollWheelZoom={interactive}
        dragging={interactive}
        zoomControl={interactive && !simple}
        doubleClickZoom={interactive}
        touchZoom={interactive}
        keyboard={interactive}
        attributionControl
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          maxZoom={19}
        />
        <Fit points={bounds} />
        {fullscreen && <MapFullscreen />}
        {routes.filter((r) => r.line?.length > 1 && r.casing).map((r) => (
          <Polyline key={`${r.key}-casing`} positions={r.line} pathOptions={{ color: r.casing, weight: 9, opacity: 0.9 }} />
        ))}
        {routes.filter((r) => r.line?.length > 1).map((r) => (
          <Polyline key={r.key} positions={r.line} pathOptions={{ color: r.colour, weight: r.dashed ? 3 : 5, opacity: r.casing ? 1 : r.dashed ? 0.6 : 0.85, dashArray: r.dashed ? '6 8' : undefined }} />
        ))}
        {homes.filter((h) => h.coord).map((h) => (
          <CircleMarker key={h.key} center={toLatLon(h.coord)} radius={7} pathOptions={{ color: '#0B2340', weight: 2, fillColor: h.colour || '#ffffff', fillOpacity: 1 }}>
            {h.title && <Tooltip>{h.title}</Tooltip>}
          </CircleMarker>
        ))}
        {stops.filter((s) => s.coord).map((s) => (
          <Marker
            key={s.key}
            position={toLatLon(s.coord)}
            icon={numberIcon(s.label, s.colour || '#0B2340')}
            eventHandlers={s.onClick && interactive ? { click: s.onClick } : undefined}
            keyboard={false}
          >
            {s.title && interactive && <Tooltip direction="top" offset={[0, -12]}>{s.title}</Tooltip>}
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
